/**
 * Google Analytics 4 Event Tracking Utility
 *
 * Provides type-safe event tracking for the PropInvest website.
 * Events are sent to GA4 when configured.
 *
 * TODO: Replace G-XXXXXXXXXX in index.html with actual GA4 tracking ID
 */

// Declare gtag as a global function (defined in index.html)
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Track a custom event in Google Analytics
 * @param eventName - Name of the event (e.g., 'property_view', 'lead_submit')
 * @param params - Optional parameters to send with the event
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

/**
 * Track a page view (usually handled automatically by GA4, but can be called manually)
 * @param path - The page path
 * @param title - The page title
 */
export function trackPageView(path: string, title?: string): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title
    });
  }
}

// ============================================
// Pre-defined Event Tracking Functions
// ============================================

/**
 * Track when a user clicks on a property card
 */
export function trackPropertyClick(propertyId: string, propertyName: string): void {
  trackEvent('property_click', {
    property_id: propertyId,
    property_name: propertyName
  });
}

/**
 * Track when a user views a property detail page
 */
export function trackPropertyView(propertyId: string, propertyName: string, province: string): void {
  trackEvent('property_view', {
    property_id: propertyId,
    property_name: propertyName,
    province: province
  });
}

/**
 * Track when a user submits a lead/contact form
 */
export function trackLeadSubmit(propertyId: string, leadQuality: string): void {
  trackEvent('lead_submit', {
    property_id: propertyId,
    lead_quality: leadQuality
  });
}

// ============================================
// Google Ads lead conversion
// ============================================

/**
 * `send_to` value for the Google Ads "Submit lead form" conversion
 * ("AW-XXXX/LABEL"). Set by TrackingScripts from the CMS
 * (tracking.googleAdsConversionId + googleAdsLeadConversionLabel); null
 * until configured, in which case trackLeadConversion() only sends the
 * GA4 generate_lead event.
 */
let googleAdsLeadSendTo: string | null = null;

export function configureGoogleAdsLeadConversion(sendTo: string | null): void {
  googleAdsLeadSendTo = sendTo && sendTo.includes('/') ? sendTo : null;
}

/**
 * Fire from a lead form's SUCCESS handler (after the server accepted the
 * submission), never on render. Sends:
 *  - GA4 `generate_lead` (recommended event) with the form source, and
 *  - the Google Ads conversion when configured in the CMS.
 *
 * History: the Ads conversion snippet used to live in the CMS "custom head
 * scripts" field, where it never executed (innerHTML scripts are inert).
 * Wiring it here is the only place it fires.
 */
export function trackLeadConversion(source: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  trackEvent('generate_lead', { lead_source: source, ...params });
  if (googleAdsLeadSendTo) {
    window.gtag('event', 'conversion', { send_to: googleAdsLeadSendTo });
  }
}

/**
 * Track when a user uses the finance calculator
 */
export function trackCalculatorUsed(propertyPrice: number): void {
  trackEvent('calculator_used', {
    property_price: propertyPrice
  });
}

/**
 * Track when a user shares a property
 */
export function trackPropertyShare(propertyId: string, platform: 'facebook' | 'twitter' | 'whatsapp'): void {
  trackEvent('property_share', {
    property_id: propertyId,
    share_platform: platform
  });
}

/**
 * Track when a user applies a filter on the properties page
 */
export function trackFilterApplied(filterType: string, filterValue: string): void {
  trackEvent('filter_applied', {
    filter_type: filterType,
    filter_value: filterValue
  });
}

/**
 * Track when a user performs a search
 */
export function trackSearch(searchQuery: string, resultsCount: number): void {
  trackEvent('search', {
    search_term: searchQuery,
    results_count: resultsCount
  });
}

/**
 * Track when a user downloads a brochure
 */
export function trackBrochureDownload(propertyId: string, propertyName: string): void {
  trackEvent('brochure_download', {
    property_id: propertyId,
    property_name: propertyName
  });
}

/**
 * Track when a user clicks a contact method (phone/email)
 */
export function trackContactClick(contactType: 'phone' | 'email', context: string): void {
  trackEvent('contact_click', {
    contact_type: contactType,
    context: context
  });
}
